const TENANT_ID_PATTERN = /^[0-9a-f]{24}$/i;

/**
 * Continuous Scan's MQTT usernames live in a reserved prefix so they cannot collide with a
 * provisioned client's `mqtt.users` title.
 *
 * A bare tenant ID is NOT a free username: fixed readers and integrations are routinely titled
 * with the tenant they serve, because that title is substituted into their authorization topic
 * templates (`dataCollection/tagHistory/${username}/${clientid}`) as the tenant routing key. When
 * Continuous Scan also claimed that name, silo/security could not tell the two credential systems
 * apart and denied every Continuous Scan connection on such a tenant (#1715).
 *
 * This module owns the prefix. Every other copy of the literal - silo/security's helpers/continuousScanUsername.js,
 * the two write guards, the one-off inspector and the MQTT workflow harness - names this file and
 * only this file as the one to keep in step with, and mqttUsernamePrefix.unit.js reads the literal
 * out of all six sources and fails if they diverge.
 */
export const CONTINUOUS_SCAN_USERNAME_PREFIX = 'cs_';

/**
 * Uses the current Backbone access token as the MQTT bearer token.
 *
 * Returns the normalized `tenantId` alongside the credentials, and callers must use *that* value
 * everywhere else in the session rather than the one they passed in.
 *
 * The tenant ID reaches the broker twice: once inside this username, and once as the `<tenant-id>`
 * segment of `stream/update/<tenant-id>/<session-id>`, which the native client builds from the
 * `tenantId` on its session config (`ContinuousScanSession.java`). silo/security derives the tenant
 * by lowercasing what it slices out of the username and then requires the topic segment to equal it
 * exactly - so if only one of those two paths is normalized, an upper-case tenant ID connects
 * successfully and is then denied on every publish and subscribe. That is #1715's symptom arriving
 * by a different route, which is why the normalized value is returned rather than applied privately
 * here: it removes the second, un-normalized path instead of leaving it to each caller to remember.
 *
 * `TENANT_ID_PATTERN` is deliberately case-insensitive, so upper case is accepted input rather than
 * a rejected one - it has to be normalized somewhere, and this is the boundary that owns it.
 *
 * @param {string} accessToken
 * @param {string} tenantId
 */
export function createMqttCredentials(accessToken, tenantId) {
    if (!TENANT_ID_PATTERN.test(tenantId)) {
        throw new Error('getMqttCredentials requires a valid tenantId.');
    }
    if (typeof accessToken !== 'string' || accessToken.length === 0) {
        throw new Error('getMqttCredentials requires an Auth0 access token.');
    }

    const normalizedTenantId = tenantId.toLowerCase();
    return {
        username: `${CONTINUOUS_SCAN_USERNAME_PREFIX}${normalizedTenantId}`,
        password: accessToken,
        tenantId: normalizedTenantId
    };
}
